/** Admin/Users.tsx — user/technician CRUD + role assignment (SPEC §3 /users). */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { createCredential, webauthnSupported } from '@/auth/webauthn';
import type { Role, User } from '@/types';

const ROLES: Role[] = ['super_admin', 'org_admin', 'technician', 'read_only', 'client'];

export default function Users() {
  const api = useApi();
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ['users'], queryFn: () => api.listUsers() });

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('technician');
  const [enrollMsg, setEnrollMsg] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createUser({ email, display_name: displayName, role }),
    onSuccess: () => {
      setEmail('');
      setDisplayName('');
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const updateRole = useMutation({
    mutationFn: (v: { id: string; role: Role }) => api.updateUser(v.id, { role: v.role }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  // WebAuthn credential enrollment for the signed-in technician (SPEC §2).
  async function enrollPasskey() {
    setEnrollMsg(null);
    if (!webauthnSupported()) {
      setEnrollMsg('This browser does not support WebAuthn.');
      return;
    }
    try {
      const begin = await api.webauthnRegisterBegin();
      const credential = await createCredential(begin.public_key.publicKey);
      await api.webauthnRegisterFinish(begin.challenge_id, credential);
      setEnrollMsg('Passkey enrolled successfully.');
    } catch (err) {
      setEnrollMsg(err instanceof Error ? err.message : 'Enrollment failed.');
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-helm-text">Users</h1>
          <p className="mt-0.5 text-sm text-helm-muted">
            Technicians and clients with access to your organization.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {enrollMsg && <span className="text-xs text-helm-muted">{enrollMsg}</span>}
          <button type="button" onClick={enrollPasskey} className="rh-btn-ghost px-3 py-1.5 text-xs">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="8" r="5" />
              <path d="M12 13v8" />
              <path d="M9 18h6" />
            </svg>
            Enroll my passkey
          </button>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="rh-card mb-6 flex flex-wrap items-end gap-4 p-5"
      >
        <div>
          <label className="mb-1.5 block text-xs font-medium text-helm-muted">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rh-input w-64"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-helm-muted">Display name</label>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="rh-input w-56" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-helm-muted">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="rh-input w-44">
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={create.isPending} className="rh-btn-primary">
          Invite user
        </button>
      </form>

      <div className="rh-card overflow-hidden">
        <table className="rh-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u: User) => (
              <tr key={u.id}>
                <td className="font-medium text-helm-text">{u.email}</td>
                <td className="text-helm-muted">{u.display_name || '—'}</td>
                <td>
                  <select
                    value={u.role}
                    onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value as Role })}
                    className="rh-input w-40"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <span className="rh-badge bg-helm-elevated text-helm-muted">{u.status}</span>
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    onClick={() => remove.mutate(u.id)}
                    className="rh-btn-ghost px-2.5 py-1 text-xs text-helm-bad hover:bg-helm-bad/10"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {isLoading && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-helm-muted">
                  Loading users…
                </td>
              </tr>
            )}
            {users.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className="py-12 text-center text-helm-muted">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
